import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import {
  Button,
  Table,
  Modal,
  Form,
} from "react-bootstrap";
import PdfLogo from "./pdflogo-sleepingowls.png";

import jsPDF from "jspdf";
import "jspdf-autotable";

import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import AnimatedPage from "./AnimatedPage";

function GatePassList() {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [gatePasses, setGatePasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedGatePasses, setSelectedGatePasses] = useState([]);
  const [deliveryStatus, setDeliveryStatus] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedGatePass, setSelectedGatePass] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedGatePass, setEditedGatePass] = useState(null);
  const [editedDate, setEditedDate] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [searchGPNo, setSearchGPNo] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [showLogo, setShowLogo] = useState(true); // Logo toggle state
  const [filterSalesChannel, setFilterSalesChannel] = useState("");
  const [filterShippingMode, setFilterShippingMode] = useState("");
  const [adminMode, setAdminMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // Number of items per page
  // const [gatePasses] = useState([...]); // Your gate passes data
  const totalPages = Math.ceil(
    gatePasses.filter((gatePass) =>
      gatePass.partyName.toLowerCase().includes(searchQuery.toLowerCase())
    ).length / pageSize
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Filter and Paginate the gate passes
  const visibleGatePasses = gatePasses
    .filter((gatePass) => {
      // 1. Soft Delete Check
      // 1. Soft Delete Check
      if (gatePass.isDeleted === "yes") return false;

      // 2. Search Query (Party Name)
      const matchesName = gatePass.partyName.toLowerCase().includes(searchQuery.toLowerCase());

      // 3. GP Number
      const matchesGPNo = searchGPNo ? gatePass.GPNo.toString().includes(searchGPNo) : true;

      // 4. Date Range
      let matchesDate = true;
      if (dateRange.start && dateRange.end) {
        const gpDate = gatePass.date instanceof Date
          ? gatePass.date
          : gatePass.date?.toDate ? gatePass.date.toDate() : null;

        if (gpDate) {
          const start = new Date(dateRange.start);
          const end = new Date(dateRange.end);
          end.setHours(23, 59, 59, 999); // Include entire end day
          matchesDate = gpDate >= start && gpDate <= end;
        }
      }

      // 5. Sales Channel Filter
      const matchesSalesChannel = filterSalesChannel ? gatePass.salesChannel === filterSalesChannel : true;

      // 6. Shipping Mode Filter
      // Note: Old records might not have shippingMode, so they will be excluded if a specific mode is selected
      const matchesShippingMode = filterShippingMode ? gatePass.shippingMode === filterShippingMode : true;

      return matchesName && matchesGPNo && matchesDate && matchesSalesChannel && matchesShippingMode;
    })
    .slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to the first page when search query changes
  };
  const [editedForm, setEditedForm] = useState({
    partyName: "",
    GPNo: "",
    gpcomment: "",
    salesChannel: "",
    shippingMode: "",
    approvedBy: "",
    otherApprovedBy: "",
    invoiceBy: "",
    otherInvoiceBy: "",
    items: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const handleItemChange = (index, e) => {
    const updatedItems = [...editedForm.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [e.target.name]: e.target.value,
    };
    setEditedForm((prevForm) => ({
      ...prevForm,
      items: updatedItems,
    }));
  };

  const handleAddItem = () => {
    setEditedForm((prevForm) => ({
      ...prevForm,
      items: [
        ...prevForm.items,
        { itemName: "", packingStyle: "", quantity: "", rate: "", gst: "" },
      ],
    }));
  };

  const handleEdit = async () => {
    try {
      // Update the gate pass details in Firestore
      await db
        .collection("gatePasses")
        .doc(editedGatePass.id)
        .update({
          partyName: editedForm.partyName,
          GPNo: editedForm.GPNo,
          items: editedForm.items,
          date: new Date(editedDate),
          comment: editedForm.gpcomment,
          salesChannel: editedForm.salesChannel,
          shippingMode: editedForm.shippingMode,
          approvedBy: editedForm.approvedBy === "Others" ? editedForm.otherApprovedBy : editedForm.approvedBy,
          invoiceBy: editedForm.invoiceBy === "Others" ? editedForm.otherInvoiceBy : editedForm.invoiceBy,
          // Add other fields as needed
        });

      // Generate and upload the updated PDF
      generatePDF();

      // Close the edit modal
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating gate pass details:", error);
    }
  };
  useEffect(() => {
    setLoading(true);
    // Set up a real-time listener to fetch and sync gatepasses locally based on adminMode
    let query = db.collection("gatePasses").orderBy("GPNo", "desc");
    if (!adminMode) {
      query = query.limit(400);
    }

    const unsubscribe = query.onSnapshot(
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Sort gate passes by GP number in descending order
        const sortedGatePasses = data.sort((a, b) => b.GPNo - a.GPNo);
        setGatePasses(sortedGatePasses);
        setLoading(false);
      },
      (error) => {
        console.error("Error listening to gate passes:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [adminMode]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedGatePasses(selectAll ? [] : [...gatePasses]);
  };

  const toggleSelectGatePass = (gatePass) => {
    if (selectedGatePasses.includes(gatePass)) {
      setSelectedGatePasses(
        selectedGatePasses.filter((selected) => selected !== gatePass)
      );
    } else {
      setSelectedGatePasses([...selectedGatePasses, gatePass]);
    }
  };

  const handleDeleteSelected = async () => {
    // Soft Delete selected gate passes
    try {
      const batch = db.batch();
      selectedGatePasses.forEach((gatePass) => {
        const docRef = db.collection("gatePasses").doc(gatePass.id);
        batch.update(docRef, { isDeleted: "yes" });
      });
      await batch.commit();

      // No longer deleting PDFs from Storage since we are soft deleting data
      // and not using storage for new PDFs anyway.

      // Clear selected gate passes
      setSelectedGatePasses([]);
      // Reload to reflect changes (or just fetch again)
      window.location.reload();
    } catch (error) {
      console.error("Error deleting gate passes:", error);
      showToast("Error deleting gate passes: " + error.message, "danger");
    }
  };

  const handleSortOrderChange = (columnName) => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);

    const sortedGatePasses = [...gatePasses].sort((a, b) => {
      if (columnName === "GPNo" || columnName === "date") {
        return sortOrder === "asc"
          ? a[columnName] - b[columnName]
          : b[columnName] - a[columnName];
      } else if (columnName === "partyName") {
        return sortOrder === "asc"
          ? a[columnName].localeCompare(b[columnName])
          : b[columnName].localeCompare(a[columnName]);
      } else if (columnName === "items") {
        return sortOrder === "asc"
          ? a[columnName].length - b[columnName].length
          : b[columnName].length - a[columnName].length;
      }
      return 0;
    });

    setGatePasses(sortedGatePasses);
  };

  const openDetailsModal = (gatePass) => {
    setSelectedGatePass(gatePass);
  };

  const closeDetailsModal = () => {
    setSelectedGatePass(null);
  };

  const downloadPDF = async (gatePass) => {
    try {
      const doc = new jsPDF();

      // --- HEADER ---
      // --- HEADER ---
      // Company Logo
      if (showLogo) {
        doc.addImage(PdfLogo, "PNG", 14, 10, 60, 24);
      }

      // Doc Title & Meta
      doc.setTextColor(0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("GATE PASS", 196, 20, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`GP NO: ${gatePass.GPNo}`, 196, 28, { align: "right" });

      // Handle Date 
      let dateStr = "";
      let timeStr = "";
      if (gatePass.date instanceof Date) {
        dateStr = gatePass.date.toLocaleDateString("en-GB");
        timeStr = gatePass.date.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' });
      } else if (gatePass.date && gatePass.date.toDate) {
        const d = gatePass.date.toDate();
        dateStr = d.toLocaleDateString("en-GB");
        timeStr = d.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' });
      }
      doc.text(`Date: ${dateStr}`, 196, 33, { align: "right" });
      doc.text(`Time: ${timeStr}`, 196, 38, { align: "right" });

      // --- INFO SECTION ---
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(14, 42, 196, 42);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("SHIP TO:", 14, 50);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(gatePass.partyName || "N/A", 14, 56);

      const startYInfo = 64;

      // --- ITEMS TABLE ---
      const tableColumn = ["#", "Box No", "Item Name", "Packing", "Qty", "Rate", "GST"];
      const tableRows = (gatePass.items || []).map((item, index) => [
        index + 1,
        item.boxnumber || "-",
        item.itemName || "-",
        item.packingStyle || "-",
        item.quantity || "0",
        item.rate || "-",
        item.gst || "-"
      ]);

      doc.autoTable({
        startY: startYInfo + 8,
        head: [tableColumn],
        body: tableRows,
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          textColor: 0,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [240, 240, 240], // Light Gray Header
          textColor: 0,
          fontStyle: 'bold',
          lineWidth: { bottom: 0.1 },
          lineColor: 0
        },
        bodyStyles: {
          lineWidth: { bottom: 0.1 },
          lineColor: 220
        },
        columnStyles: {
          0: { cellWidth: 10 },
          4: { halign: 'center' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        }
      });

      // --- FOOTER / TOTALS ---
      let finalY = doc.lastAutoTable.finalY + 10;

      // Calculate Total Qty
      const rawTotalQty = (gatePass.items || []).reduce((sum, item) => sum + (parseFloat(item.quantity || 0) || 0), 0);
      const totalQty = Number.isInteger(rawTotalQty) ? rawTotalQty : parseFloat(rawTotalQty.toFixed(3));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(`Total Qty: ${totalQty}`, 196, finalY, { align: "right" });

      // Remarks
      finalY += 10;
      if (gatePass.comment) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.text(`Remarks: ${gatePass.comment}`, 14, finalY);
        finalY += 10;
      }

      // Signatures
      finalY += 20; // Space for signing
      const pageHeight = doc.internal.pageSize.height;

      if (finalY > pageHeight - 40) {
        doc.addPage();
        finalY = 40;
      }

      doc.setLineWidth(0.1);

      // 1. Account's Approval
      const ySigLine = finalY + 15;
      doc.line(14, ySigLine, 60, ySigLine);
      doc.setFont("helvetica", "bold");
      doc.fontSize = 8;
      doc.text("Account's Approval", 14, ySigLine + 5);
      if (gatePass.approvedBy) {
        doc.setFont("helvetica", "normal");
        // Added margin by increasing Y
        doc.text(`Approved by : ${gatePass.approvedBy}`, 14, ySigLine + 16);
      } else if (gatePass.otherApprovedBy) {
        doc.setFont("helvetica", "normal");
        doc.text(`Approved by : ${gatePass.otherApprovedBy}`, 14, ySigLine + 16);
      }


      // 2. Gate Approval
      doc.line(75, ySigLine, 125, ySigLine);
      doc.setFont("helvetica", "bold");
      doc.text("Gate Approval", 75, ySigLine + 5);

      // 3. Received By
      doc.line(140, ySigLine, 196, ySigLine);
      doc.text("Received By (Sign & Stamp)", 140, ySigLine + 5);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Name/Phone:", 140, ySigLine + 10);
      doc.text("Vehicle No:", 140, ySigLine + 15);


      // Save PDF via Blob to open in new tab
      const blob = doc.output("blob");
      const blobURL = URL.createObjectURL(blob);
      window.open(blobURL, "_blank");

    } catch (error) {
      console.error("Error generating Gate Pass PDF:", error);
      showToast("Could not generate PDF. Please check console.", "danger");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSearchGPNo("");
    setDateRange({ start: "", end: "" });
    setFilterSalesChannel("");
    setFilterShippingMode("");
    setAdminMode(false);
  };
  const generatePDF = () => {
    try {
      const doc = new jsPDF();
      const data = {
        ...editedForm,
        comment: editedForm.gpcomment,
        date: new Date(editedDate)
      };

      // --- HEADER ---
      if (showLogo) {
        doc.addImage(PdfLogo, "PNG", 14, 10, 60, 24);
      }

      doc.setTextColor(0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("GATE PASS", 196, 20, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`GP NO: ${data.GPNo}`, 196, 28, { align: "right" });

      let dateStr = "";
      let timeStr = "";
      if (data.date instanceof Date) {
        dateStr = data.date.toLocaleDateString("en-GB");
        timeStr = data.date.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' });
      }
      doc.text(`Date: ${dateStr}`, 196, 33, { align: "right" });
      doc.text(`Time: ${timeStr}`, 196, 38, { align: "right" });

      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(14, 42, 196, 42);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("SHIP TO:", 14, 50);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(data.partyName || "N/A", 14, 56);

      const startYInfo = 64;

      const tableColumn = ["#", "Box No", "Item Name", "Packing", "Qty", "Rate", "GST"];
      const tableRows = (data.items || []).map((item, index) => [
        index + 1,
        item.boxnumber || "-",
        item.itemName || "-",
        item.packingStyle || "-",
        item.quantity || "0",
        item.rate || "-",
        item.gst || "-"
      ]);

      doc.autoTable({
        startY: startYInfo + 8,
        head: [tableColumn],
        body: tableRows,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 3, textColor: 0, overflow: 'linebreak' },
        headStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold', lineWidth: { bottom: 0.1 }, lineColor: 0 },
        bodyStyles: { lineWidth: { bottom: 0.1 }, lineColor: 220 },
        columnStyles: {
          0: { cellWidth: 10 },
          4: { halign: 'center' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        }
      });

      let finalY = doc.lastAutoTable.finalY + 10;
      const rawTotalQty = (data.items || []).reduce((sum, item) => sum + (parseFloat(item.quantity || 0) || 0), 0);
      const totalQty = Number.isInteger(rawTotalQty) ? rawTotalQty : parseFloat(rawTotalQty.toFixed(3));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(`Total Qty: ${totalQty}`, 196, finalY, { align: "right" });

      finalY += 10;
      if (data.comment) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.text(`Remarks: ${data.comment}`, 14, finalY);
        finalY += 10;
      }

      finalY += 20;
      if (finalY > doc.internal.pageSize.height - 40) {
        doc.addPage();
        finalY = 40;
      }
      doc.setLineWidth(0.1);

      const ySigLine = finalY + 15;
      doc.line(14, ySigLine, 60, ySigLine);
      doc.setFont("helvetica", "bold");
      doc.fontSize = 8;
      doc.text("Account's Approval", 14, ySigLine + 5);
      if (data.approvedBy) {
        doc.setFont("helvetica", "normal");
        const approver = data.approvedBy === "Others" ? data.otherApprovedBy : data.approvedBy;
        doc.text(`Approved by : ${approver}`, 14, ySigLine + 16);
      }

      doc.line(75, ySigLine, 125, ySigLine);
      doc.setFont("helvetica", "bold");
      doc.text("Gate Approval", 75, ySigLine + 5);

      doc.line(140, ySigLine, 196, ySigLine);
      doc.text("Received By (Sign & Stamp)", 140, ySigLine + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Name/Phone:", 140, ySigLine + 10);
      doc.text("Vehicle No:", 140, ySigLine + 15);


      // Save the PDF to Firebase Storage
      const pdfFileName = `${data.GPNo}-${data.partyName}-GatePass.pdf`;
      const storageRef = storage.ref();
      const pdfFileRef = storageRef.child(pdfFileName);
      const pdfBlob = doc.output("blob");

      // Upload the Blob to Firebase Storage
      pdfFileRef.put(pdfBlob).then(() => {
        console.log("PDF uploaded to Firebase Storage");
      });
      setTimeout(() => {
        window.location.reload();
      }, 500);

    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };
  const handleDeliveryStatusChange = async (gatePassId, newStatus) => {
    try {
      // Update the gate pass delivery status in Firestore
      await db.collection("gatePasses").doc(gatePassId).update({
        DeliveryStatus: newStatus,
      });

      // Update the local state with the new delivery status
      setDeliveryStatus({
        ...deliveryStatus,
        [gatePassId]: newStatus,
      });
    } catch (error) {
      console.error("Error updating delivery status:", error);
    }
  };
  const downloadPackingSlip = async (gatePass) => {
    try {
      // Create a new jsPDF instance
      const doc = new jsPDF();

      // --- HEADER ---
      // Company Logo
      if (showLogo) {
        doc.addImage(PdfLogo, "PNG", 14, 10, 60, 24);
      }

      // Doc Title & Meta
      doc.setTextColor(0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("PACKING SLIP", 196, 20, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`PS NO: ${gatePass.GPNo}`, 196, 28, { align: "right" });

      // Handle Date 
      let dateStr = "";
      let timeStr = "";
      if (gatePass.date instanceof Date) {
        dateStr = gatePass.date.toLocaleDateString("en-GB");
        timeStr = gatePass.date.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' });
      } else if (gatePass.date && gatePass.date.toDate) {
        const d = gatePass.date.toDate();
        dateStr = d.toLocaleDateString("en-GB");
        timeStr = d.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' });
      }
      doc.text(`Date: ${dateStr}`, 196, 33, { align: "right" });
      doc.text(`Time: ${timeStr}`, 196, 38, { align: "right" });

      // --- INFO SECTION ---
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(14, 42, 196, 42);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("SHIP TO:", 14, 50);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(gatePass.partyName || "N/A", 14, 56);

      // --- ITEMS TABLE ---
      const tableColumn = ["#", "Box No", "Item Name", "Packing Style", "Qty", "Rate", "GST"];
      const tableRows = (gatePass.items || []).map((item, index) => [
        index + 1,
        item.boxnumber || "-",
        item.itemName || "-",
        item.packingStyle || "-",
        item.quantity || "0",
        item.rate || "-",
        item.gst || "-"
      ]);

      doc.autoTable({
        startY: 64,
        head: [tableColumn],
        body: tableRows,
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          textColor: 0,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: 0,
          fontStyle: 'bold',
          lineWidth: { bottom: 0.1 },
          lineColor: 0
        },
        bodyStyles: {
          lineWidth: { bottom: 0.1 },
          lineColor: 220
        },
        columnStyles: {
          0: { cellWidth: 10 },
          4: { halign: 'center' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        }
      });

      // --- FOOTER / TOTALS ---
      let finalY = doc.lastAutoTable.finalY + 10;

      // Calculate Total Qty
      const rawTotalQty = (gatePass.items || []).reduce((sum, item) => sum + (parseFloat(item.quantity || 0) || 0), 0);
      const totalQty = Number.isInteger(rawTotalQty) ? rawTotalQty : parseFloat(rawTotalQty.toFixed(3));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(`Total Qty: ${totalQty}`, 196, finalY, { align: "right" });

      // Remarks
      finalY += 10;
      if (gatePass.comment) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.text(`Remarks: ${gatePass.comment}`, 14, finalY);
        finalY += 10;
      }

      // Signatures
      finalY += 20; // Space for signing
      const pageHeight = doc.internal.pageSize.height;

      // Ideally prevent page break issue, but for packing slip usually 1 page.
      // If close to bottom, add page? For now assume it fits.
      if (finalY > pageHeight - 40) {
        doc.addPage();
        finalY = 40;
      }

      doc.setLineWidth(0.1);

      // 1. Account's Approval
      const ySigLine = finalY + 15;
      doc.line(14, ySigLine, 60, ySigLine);
      doc.setFont("helvetica", "bold");
      doc.fontSize = 8;
      doc.text("Account's Approval", 14, ySigLine + 5);

      // 2. Gate Approval
      doc.line(75, ySigLine, 125, ySigLine);
      doc.text("Gate Approval", 75, ySigLine + 5);

      // 3. Received By
      doc.line(140, ySigLine, 196, ySigLine);
      doc.text("Received By (Sign & Stamp)", 140, ySigLine + 5);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Name/Phone:", 140, ySigLine + 10);
      doc.text("Vehicle No:", 140, ySigLine + 15);


      // Save PDF via Blob to open in new tab (modern browser friendly)
      const blob = doc.output("blob");
      const blobURL = URL.createObjectURL(blob);
      window.open(blobURL, "_blank");

    } catch (error) {
      console.error("Error generating packing slip:", error);
      showToast("Could not generate Packing Slip. Please check console.", "danger");
    }
  };

  return (
    <AnimatedPage>
      <div className="min-h-screen py-8 px-4 md:px-8 relative">
      {/* Square Grid Background Layer */}
      <div className="fixed inset-0 h-full w-full -z-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#78aefd] shadow-sm border border-blue-100">
              <i className="fa-solid fa-list-ul text-2xl"></i>
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-800 mb-0">{t("gatePassList")}</h1>
              <p className="text-blue-600/60 text-sm font-medium m-0">{t("gatepassOverview")}</p>
            </div>
          </div>
        </div>

        {/* Search & Filter Section */}
        <div className="glass-card border border-white/60 rounded-3xl shadow-xl mb-8 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Party Name */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-1">{t("partyName")}</label>
              <div className="relative">
                <i className="fa fa-search absolute left-4 top-3 text-slate-300 text-sm"></i>
                <input
                  type="text"
                  placeholder={t("searchParty")}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="flex h-11 w-full rounded-xl border border-blue-50 bg-white/60 pl-10 pr-4 py-2 text-sm form-input-focus shadow-sm"
                />
              </div>
            </div>

            {/* GP Number */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-1">{t("searchGpNo")}</label>
              <input
                type="text"
                placeholder={t("searchGpNo")}
                value={searchGPNo}
                onChange={(e) => setSearchGPNo(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-blue-50 bg-white/60 px-4 py-2 text-sm form-input-focus shadow-sm"
              />
            </div>

            {/* Date Range */}
            <div className="space-y-1.5 md:col-span-2 lg:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-1">{t("dateRange")}</label>
              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="h-11 w-full rounded-xl border border-blue-50 bg-white/60 px-3 py-2 text-xs form-input-focus shadow-sm"
                />
                <span className="text-slate-300">{t("to")}</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="h-11 w-full rounded-xl border border-blue-50 bg-white/60 px-3 py-2 text-xs form-input-focus shadow-sm"
                />
              </div>
            </div>

            {/* Sales Channel */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-1">{t("salesChannel")}</label>
              <select
                value={filterSalesChannel}
                onChange={(e) => setFilterSalesChannel(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-blue-50 bg-white/60 px-4 py-2 text-sm form-input-focus appearance-none cursor-pointer"
              >
                <option value="">{t("allChannels")}</option>
                <option value="Factory (Job work etc.)">{t("factoryChannel")}</option>
                <option value="Indiamart/Retail/Whitelabelling (Sneha & Kashish)">{t("indiamartChannel")}</option>
                <option value="Website">{t("websiteChannel")}</option>
                <option value="Operations (Rajesh & Raj)">{t("operationsChannel")}</option>
              </select>
            </div>

            {/* Shipping Mode & Clear */}
            <div className="flex items-end gap-2">
              <div className="space-y-1.5 flex-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-1">{t("shipping")}</label>
                <select
                  value={filterShippingMode}
                  onChange={(e) => setFilterShippingMode(e.target.value)}
                  className="flex h-11 w-full rounded-xl border border-blue-50 bg-white/60 px-4 py-2 text-sm form-input-focus"
                >
                  <option value="">{t("allModes")}</option>
                  <option value="Transport">{t("transportMode")}</option>
                  <option value="Courier">{t("courierMode")}</option>
                  <option value="Tempo / Porter">{t("tempoMode")}</option>
                  <option value="Customer Pickup">{t("pickupMode")}</option>
                </select>
              </div>
              <button
                onClick={clearFilters}
                title={t("clearFilters")}
                className="h-11 px-4 rounded-xl border border-blue-100 bg-white text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors shadow-sm"
              >
                <i className="fa fa-filter-circle-xmark"></i>
              </button>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-blue-50 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={adminMode}
                  onChange={(e) => {
                    if (e.target.checked) {
                      showToast(t("showAllWarning"), "warning");
                    }
                    setAdminMode(e.target.checked);
                  }}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-400"></div>
                <span className="ml-3 text-xs font-bold text-slate-600 uppercase">{t("showAllGatepasses")}</span>
              </label>

              {selectedGatePasses.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2 px-3 py-1 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 text-xs font-bold transition-all"
                >
                  <i className="fa fa-trash"></i> {t("deleteSelected")} ({selectedGatePasses.length})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="glass-card border border-white/60 rounded-3xl shadow-2xl overflow-hidden">
          <div className="table-container overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead className="bg-blue-50/80 text-blue-900/70 border-b border-blue-100">
                <tr>
                  <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] text-center w-12">
                    <input
                      type="checkbox"
                      className="rounded border-blue-200"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] cursor-pointer" onClick={() => handleSortOrderChange("GPNo")}>
                    {t("gpNoHeader")} {sortOrder === 'asc' ? '▲' : '▼'}
                  </th>
                  <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] cursor-pointer" onClick={() => handleSortOrderChange("partyName")}>
                    {t("partyNameHeader")} {sortOrder === 'asc' ? '▲' : '▼'}
                  </th>
                  <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] cursor-pointer" onClick={() => handleSortOrderChange("date")}>
                    {t("dateTimeHeader")} {sortOrder === 'asc' ? '▲' : '▼'}
                  </th>
                  <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] text-center">{t("itemsHeader")}</th>
                  <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] text-center">{t("docsHeader")}
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <input
                        type="checkbox"
                        checked={showLogo}
                        onChange={(e) => setShowLogo(e.target.checked)}
                        className="rounded border-blue-200 text-blue-600 focus:ring-0 w-3 h-3"
                        title="Include Logo in PDF"
                      />
                      <span className="text-[9px] text-slate-400 font-normal normal-case">Logo</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] text-center">{t("statusHeader")}</th>
                  <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] text-right">{t("actionsHeader")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-50/50">
                {loading ? (
                  [...Array(pageSize)].map((_, index) => (
                    <tr key={index} className="animate-pulse">
                      <td className="px-6 py-4 text-center">
                        <div className="w-4 h-4 bg-slate-200 rounded mx-auto"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-200 rounded w-16"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-200 rounded w-48"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-200 rounded w-28"></div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="h-7 bg-slate-200 rounded-lg w-20 mx-auto"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-2">
                          <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
                          <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="h-6 bg-slate-200 rounded-full w-16 mx-auto"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
                          <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : visibleGatePasses.length > 0 ? (
                  visibleGatePasses.map((gatePass) => (
                    <tr key={gatePass.id} className="hover:bg-white/50 transition-colors">
                      <td className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          className="rounded border-blue-100"
                          checked={selectedGatePasses.includes(gatePass)}
                          onChange={() => toggleSelectGatePass(gatePass)}
                        />
                      </td>
                      <td className="px-6 py-4 font-bold text-blue-600">{gatePass.GPNo}</td>
                      <td className="px-6 py-4 font-semibold text-slate-700 uppercase tracking-tight">{gatePass.partyName}</td>
                      <td className="px-6 py-4 text-xs font-medium text-slate-500 italic">
                        {gatePass.date instanceof Date
                          ? `${gatePass.date.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })} - ${gatePass.date.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}`
                          : gatePass.date?.toDate
                            ? `${gatePass.date.toDate().toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })} - ${gatePass.date.toDate().toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}`
                            : ""}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => openDetailsModal(gatePass)}
                          className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-bold active:scale-95 transition-transform"
                        >
                          {t("itemsCount", { count: gatePass.items ? gatePass.items.length : 0 })}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-2">
                          <button
                            title={t("downloadPdf")}
                            onClick={() => downloadPDF(gatePass)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-all hover:scale-110 active:scale-95"
                          >
                            <i className="fa fa-file-pdf"></i>
                          </button>
                          <button
                            title={t("packingSlip")}
                            onClick={() => downloadPackingSlip(gatePass)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-all hover:scale-110 active:scale-95"
                          >
                            <i className="fa fa-box-open text-xs"></i>
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <select
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border-none focus:ring-0 ${(deliveryStatus[gatePass.id] || gatePass.DeliveryStatus) === "Delivered" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                            }`}
                          style={{ appearance: 'none', textAlign: 'center' }}
                          value={deliveryStatus[gatePass.id] || gatePass.DeliveryStatus || "NotDelivered"}
                          onChange={(e) => handleDeliveryStatusChange(gatePass.id, e.target.value)}
                        >
                          <option value="NotDelivered">{t("pendingStatus")}</option>
                          <option value="Delivered">{t("deliveredStatus")}</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            title={t("duplicate")}
                            onClick={() => {
                              navigate("/", {
                                state: {
                                  duplicateData: {
                                    ...gatePass,
                                    GPNo: "",
                                    date: ""
                                  }
                                }
                              });
                            }}
                            className="w-8 h-8 bg-white border border-slate-100 text-slate-400 hover:text-blue-500 hover:border-blue-200 rounded-lg shadow-sm transition-all hover:scale-110 active:scale-95"
                          >
                            <i className="fa fa-copy text-xs"></i>
                          </button>
                          <button
                            title={t("edit")}
                            onClick={() => {
                              setEditedGatePass(gatePass);
                              const standardNames = ["Rajkumar", "Rajesh", "Kashish", "Sneha", "Lucky"];
                              let approvedByValue = gatePass.approvedBy || "";
                              let otherApprovedByValue = "";
                              if (approvedByValue && !standardNames.includes(approvedByValue)) {
                                otherApprovedByValue = approvedByValue;
                                approvedByValue = "Others";
                              }
                              let invoiceByValue = gatePass.invoiceBy || "";
                              let otherInvoiceByValue = "";
                              if (invoiceByValue && !standardNames.includes(invoiceByValue)) {
                                otherInvoiceByValue = invoiceByValue;
                                invoiceByValue = "Others";
                              }
                              setEditedForm({
                                partyName: gatePass.partyName,
                                GPNo: gatePass.GPNo,
                                gpcomment: gatePass.comment,
                                salesChannel: gatePass.salesChannel || "",
                                shippingMode: gatePass.shippingMode || "",
                                items: [...gatePass.items],
                                approvedBy: approvedByValue,
                                otherApprovedBy: otherApprovedByValue,
                                invoiceBy: invoiceByValue,
                                otherInvoiceBy: otherInvoiceByValue,
                              });
                              let d = new Date();
                              if (gatePass.date instanceof Date) {
                                d = gatePass.date;
                              } else if (gatePass.date && gatePass.date.toDate) {
                                d = gatePass.date.toDate();
                              }
                              try {
                                const formattedDate = d.toISOString().substring(0, 10);
                                setEditedDate(formattedDate);
                              } catch (e) {
                                console.error(e);
                                setEditedDate("");
                              }
                              setShowEditModal(true);
                            }}
                            className="w-8 h-8 bg-white border border-slate-100 text-slate-400 hover:text-amber-500 hover:border-amber-200 rounded-lg shadow-sm transition-all hover:scale-110 active:scale-95"
                          >
                            <i className="fa fa-pen text-xs"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-slate-400 font-semibold bg-white/30">
                      {t("noItemsAvailable")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="px-6 py-6 bg-slate-50/50 border-t border-blue-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
              {t("pageIndicator", { current: currentPage, total: totalPages })}
            </div>
            <nav className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-blue-50 text-slate-400 hover:text-blue-500 hover:border-blue-200 transition-all shadow-sm disabled:opacity-50"
              >
                <i className="fa fa-chevron-left text-xs"></i>
              </button>

              {[...Array(totalPages)].map((_, index) => {
                if (totalPages > 10 && Math.abs(currentPage - (index + 1)) > 2 && index !== 0 && index !== totalPages - 1) {
                  if (Math.abs(currentPage - (index + 1)) === 3) return <span key={index} className="px-2 text-slate-300">...</span>;
                  return null;
                }
                return (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`w-9 h-9 flex items-center justify-center rounded-xl font-bold transition-all shadow-sm ${currentPage === index + 1
                      ? "bg-blue-500 text-white shadow-blue-200"
                      : "bg-white border border-blue-50 text-slate-600 hover:bg-blue-50"
                      }`}
                  >
                    {index + 1}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-blue-50 text-slate-400 hover:text-blue-500 hover:border-blue-200 transition-all shadow-sm disabled:opacity-50"
              >
                <i className="fa fa-chevron-right text-xs"></i>
              </button>
            </nav>
          </div>
        </div>

        {/* Footer Credits */}
        <div className="mt-12 text-center pb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700/40">{t("rightsReserved")}</p>
        </div>
      </div>

      {/* Modal for displaying item details (Using existing React-Bootstrap Modal) */}
      <Modal show={selectedGatePass !== null} onHide={closeDetailsModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("itemsHeader")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGatePass && selectedGatePass.items.length > 0 ? (
            <Table striped bordered hover className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("itemName")}</th>
                  <th>{t("packingStyle")}</th>
                  <th>{t("qty")}</th>
                  <th>{t("rate")}</th>
                  <th>{t("gst")}</th>
                </tr>
              </thead>
              <tbody>
                {selectedGatePass.items.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.itemName}</td>
                    <td>{item.packingStyle}</td>
                    <td>{item.quantity}</td>
                    <td>{item.rate}</td>
                    <td>{item.gst}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>{t("noItemsAvailable")}</p>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal for editing gate pass details */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("editGatePass")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="formPartyName" className="mb-3">
                  <Form.Label>{t("partyName")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="partyName"
                    value={editedForm.partyName}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="formGPNo" className="mb-3">
                  <Form.Label>{t("gpNoHeader")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="GPNo"
                    value={editedForm.GPNo}
                    readOnly
                    disabled
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="formDate" className="mb-3">
                  <Form.Label>{t("date")}</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={editedDate}
                    onChange={(e) => setEditedDate(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="gpcomment" className="mb-3">
                  <Form.Label>{t("comment")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="gpcomment"
                    value={editedForm.gpcomment}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="editSalesChannel" className="mb-3">
                  <Form.Label>{t("salesChannel")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="salesChannel"
                    value={editedForm.salesChannel}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("selectChannel")}</option>
                    <option value="Factory (Job work etc.)">{t("factoryChannel")}</option>
                    <option value="Indiamart/Retail/Whitelabelling (Sneha & Kashish)">{t("indiamartChannel")}</option>
                    <option value="Website">{t("websiteChannel")}</option>
                    <option value="Operations (Rajesh & Raj)">{t("operationsChannel")}</option>
                  </Form.Control>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="editShippingMode" className="mb-3">
                  <Form.Label>{t("shippingMode")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="shippingMode"
                    value={editedForm.shippingMode}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("selectMode")}</option>
                    <option value="Transport">{t("transportMode")}</option>
                    <option value="Courier">{t("courierMode")}</option>
                    <option value="Tempo / Porter">{t("tempoMode")}</option>
                    <option value="Customer Pickup">{t("pickupMode")}</option>
                  </Form.Control>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="editApprovedBy" className="mb-3">
                  <Form.Label>{t("approvedBy")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="approvedBy"
                    value={editedForm.approvedBy}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("selectApprover")}</option>
                    <option value="Rajkumar">Rajkumar</option>
                    <option value="Rajesh">Rajesh</option>
                    <option value="Kashish">Kashish</option>
                    <option value="Sneha">Sneha</option>
                    <option value="Lucky">Lucky</option>
                    <option value="Others">{t("others")}</option>
                  </Form.Control>
                </Form.Group>
                {editedForm.approvedBy === "Others" && (
                  <Form.Group controlId="editOtherApprovedBy" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder={t("enterName")}
                      name="otherApprovedBy"
                      value={editedForm.otherApprovedBy}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                )}
              </div>
              <div className="col-md-6">
                <Form.Group controlId="editInvoiceBy" className="mb-3">
                  <Form.Label>{t("invoiceBy")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="invoiceBy"
                    value={editedForm.invoiceBy}
                    onChange={handleInputChange}
                  >
                    <option value="">{t("selectIssuer")}</option>
                    <option value="Rajkumar">Rajkumar</option>
                    <option value="Rajesh">Rajesh</option>
                    <option value="Kashish">Kashish</option>
                    <option value="Sneha">Sneha</option>
                    <option value="Lucky">Lucky</option>
                    <option value="Others">{t("others")}</option>
                  </Form.Control>
                </Form.Group>
                {editedForm.invoiceBy === "Others" && (
                  <Form.Group controlId="editOtherInvoiceBy" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder={t("enterName")}
                      name="otherInvoiceBy"
                      value={editedForm.otherInvoiceBy}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                )}
              </div>
            </div>

            <h5 className="mt-4 mb-3 font-bold text-slate-700">{t("itemsHeader")}</h5>
            <div className="table-responsive bg-slate-50 p-2 rounded-xl border border-slate-200">
              <Table bordered hover className="mb-0 bg-white">
                <thead>
                  <tr className="text-small">
                    <th style={{ width: '60px' }}>{t("boxNo")}</th>
                    <th>{t("itemName")}</th>
                    <th>{t("packingStyle")}</th>
                    <th style={{ width: '80px' }}>{t("qty")}</th>
                    <th style={{ width: '100px' }}>{t("rate")}</th>
                    <th style={{ width: '80px' }}>{t("gst")}</th>
                    <th style={{ width: '80px' }}>{t("action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {editedForm.items.map((item, index) => (
                    <tr key={index}>
                      <td className="p-1">
                        <Form.Control
                          size="sm"
                          type="text"
                          name="boxnumber"
                          value={item.boxnumber}
                          onChange={(e) => handleItemChange(index, e)}
                        />
                      </td>
                      <td className="p-1">
                        <Form.Control
                          size="sm"
                          type="text"
                          name="itemName"
                          value={item.itemName}
                          onChange={(e) => handleItemChange(index, e)}
                        />
                      </td>
                      <td className="p-1">
                        <Form.Control
                          size="sm"
                          type="text"
                          name="packingStyle"
                          value={item.packingStyle}
                          onChange={(e) => handleItemChange(index, e)}
                        />
                      </td>
                      <td className="p-1">
                        <Form.Control
                          size="sm"
                          type="text"
                          name="quantity"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, e)}
                        />
                      </td>
                      <td className="p-1">
                        <Form.Control
                          size="sm"
                          type="text"
                          name="rate"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, e)}
                        />
                      </td>
                      <td className="p-1">
                        <Form.Control
                          size="sm"
                          type="text"
                          name="gst"
                          value={item.gst}
                          onChange={(e) => handleItemChange(index, e)}
                        />
                      </td>
                      <td className="p-1 text-center">
                        <Button
                          variant="link"
                          className="text-danger p-0"
                          onClick={() => {
                            const updatedItems = [...editedForm.items];
                            updatedItems.splice(index, 1);
                            setEditedForm((prevForm) => ({
                              ...prevForm,
                              items: updatedItems,
                            }));
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <Button variant="outline-primary" size="sm" className="mt-2" onClick={handleAddItem}>
              <i className="fa fa-plus-circle me-1"></i> {t("addItem")}
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            {t("close")}
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            {t("save")}
          </Button>
        </Modal.Footer>
      </Modal>


      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-[#4580d6] text-white shadow-xl hover:bg-[#609dfa] flex items-center justify-center transition-all duration-300 scale-100 hover:scale-110 active:scale-95 z-50 border border-white/20"
          title="Scroll to Top"
        >
          <i className="fa fa-arrow-up text-lg"></i>
        </button>
      )}
    </div>
    </AnimatedPage>
  );
}

export default GatePassList;
